import { graphql } from 'react-relay';

import { query } from '@confluence/query-preloader-tools/entry-points/graphql.ts';

import { writeQueryInterop } from '@confluence/apollo-relay-interop/entry-points/writeQuery.ts';
import { expValEquals } from '@confluence/feature-experiments';

import WebItemLocationQuery, {
	type WebItemLocationQuery$data,
	type WebItemLocationQuery$variables,
} from './__generated__/WebItemLocationQuery.graphql.ts';

import type {
	preloadWebItemLocationWithSpaceKeyQuery$data,
	preloadWebItemLocationWithSpaceKeyQuery$variables,
} from './__generated__/preloadWebItemLocationWithSpaceKeyQuery.graphql.ts';

const preloadWebItemLocationWithSpaceKeyQuery = graphql`
	query preloadWebItemLocationWithSpaceKeyQuery(
		$location: String
		$locations: [String]
		$spaceKey: String
		$version: Int
	) {
		space(key: $spaceKey) {
			id
			homepageId
			homepageWebSections(location: $location, locations: $locations, version: $version) {
				id
				label
				cacheKey
				styleClass
				items {
					accessKey
					completeKey
					hasCondition
					id
					icon {
						height
						width
						path
					}
					params {
						key
						value
					}
					label
					moduleKey
					section
					styleClass
					tooltip
					url
					urlWithoutContextPath
					weight
				}
			}
		}
	}
`;

export function preloadWebItemLocation({
	location = null,
	contentId = null,
	spaceKey = null,
	version = null,
}: {
	location?: string | string[] | null;
	contentId?: string | null;
	spaceKey?: string | null;
	version?: number | null;
} = {}) {
	if (!contentId && spaceKey) {
		return query<
			preloadWebItemLocationWithSpaceKeyQuery$data,
			preloadWebItemLocationWithSpaceKeyQuery$variables
		>({
			query: preloadWebItemLocationWithSpaceKeyQuery,
			variables: {
				location: Array.isArray(location) ? null : location,
				locations: Array.isArray(location) ? location : [],
				spaceKey,
				version,
			},
		}).then(({ data }) => {
			if (data?.space?.homepageId) {
				writeQueryInterop<WebItemLocationQuery$data, WebItemLocationQuery$variables>({
					query: WebItemLocationQuery,
					variables: {
						location: Array.isArray(location) ? null : location,
						locations: Array.isArray(location) ? location : [],
						contentId: data?.space?.homepageId,
						spaceKey: null,
						version,
					},
					data: {
						webItemSections: data?.space?.homepageWebSections || [],
					},
				});
			}
		});
	}
	const variables = {
		// GraphQL doesn't cache single location strings properly when passed to locations
		// unless we're using multiple locations we should be using the location variable
		location: Array.isArray(location) ? null : location,
		locations: Array.isArray(location) ? location : [],
		contentId,
		spaceKey,
		version,
	};

	const result = query<WebItemLocationQuery$data, WebItemLocationQuery$variables>({
		query: WebItemLocationQuery,
		variables,
	});

	// When fetching multiple locations in a single batched request, the result is cached
	// under the combined array key { location: null, locations: [...] }. However, consumers
	// (e.g. <WebItemLocation location={SYSTEM_USER} />) query with individual location keys
	// { location: 'system.user', locations: [] }, causing a cache miss.
	// To fix this, we split the batched result and write each location's sections back into
	// the cache under its individual key so consumers get a cache hit.
	// Split and store in cache for any location batch.
	if (expValEquals('web_item_query_batch_system_call', 'isEnabled', true)) {
		if (Array.isArray(location)) {
			result.then(({ data }) => {
				if (data?.webItemSections) {
					// Write batched cache key with all sections so consumers using
					// the array variables also get a cache hit
					writeQueryInterop<WebItemLocationQuery$data, WebItemLocationQuery$variables>({
						query: WebItemLocationQuery,
						variables: {
							location: null,
							locations: location,
							contentId,
							spaceKey,
							version,
						},
						data: { webItemSections: data.webItemSections },
					});

					// Write individual cache keys for each location so consumers using
					// single location variables (e.g. <WebItemLocation location={SYSTEM_USER} />)
					// also get a cache hit
					location.forEach((loc) => {
						writeQueryInterop<WebItemLocationQuery$data, WebItemLocationQuery$variables>({
							query: WebItemLocationQuery,
							variables: {
								location: loc,
								locations: [],
								contentId,
								spaceKey,
								version,
							},
							data: {
								webItemSections: data.webItemSections?.filter((section) =>
									section?.id?.includes(loc),
								),
							},
						});
					});
				}
			});
		}
	}

	return result;
}
